import { ApolloQueryResult, gql } from "@apollo/client";
import { IContentRaw } from "components/admin/custom-blocks/types";
import { BookCard } from "components/book-card";
import { Button } from "components/buttons/button";
import { RenderContent } from "components/content-renderer";
import { EventCard } from "components/event-card";
import { Layout } from "components/layout";
import { Spinner } from "components/spinner";
import {
  BookCardDataFragmentDoc,
  EventCardDataFragmentDoc,
  GetAllUpcomingEventsDocument,
  GetAllUpcomingEventsQueryVariables,
  GetPageContentDocument,
  GetPageContentQueryResult,
  GetPageContentQueryVariables,
  useGetAllUpcomingEventsQuery,
  useGetPastEventsQuery,
} from "lib/graphql-generated";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import { DateObject } from "lib/utils";
import { GetServerSideProps } from "next";
import Link from "next/link";

const GET_ALL_EVENTS = gql`
  query GetAllUpcomingEvents($today: timestamp) {
    events(
      where: {
        _and: [{ end_time: { _gte: $today } }, { is_cancelled: { _eq: false } }]
      }
      order_by: { start_time: asc }
    ) {
      event_books(limit: 1) {
        book {
          ...BookCardData
        }
      }
      end_time
      is_festival
      events {
        slug
        title
      }
      festival {
        event_books(limit: 1) {
          book {
            ...BookCardData
          }
        }
        end_time
        is_festival
        events {
          slug
          title
        }
        ...EventCardData
      }
      ...EventCardData
    }
  }
  ${EventCardDataFragmentDoc}
  ${BookCardDataFragmentDoc}
`;

const GET_ALL_PAST_EVENTS = gql`
  query GetPastEvents($today: timestamp) {
    events(
      where: {_and: [{ end_time: { _lte: $today } }, {is_cancelled: {_eq: false}}]}
      order_by: { start_time: desc }
    ) {
      ...EventCardData
    }
  ${EventCardDataFragmentDoc}
  ${BookCardDataFragmentDoc}
  }
`;

interface Props {
  today: string;
  content: IContentRaw;
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();
  const today = new Date().toUTCString();

  await client.query({
    query: GetAllUpcomingEventsDocument,
    variables: {
      today,
    } as GetAllUpcomingEventsQueryVariables,
  });

  const { data }: ApolloQueryResult<GetPageContentQueryResult["data"]> =
    await client.query({
      query: GetPageContentDocument,
      variables: {
        title: "events",
      } as GetPageContentQueryVariables,
    });

  return addApolloState(client, {
    props: {
      today,
      content: data.pages_by_pk.content,
    },
  });
};

export const Events = ({ today, content }: Props) => {
  const { data, loading } = useGetAllUpcomingEventsQuery({
    variables: {
      today,
    },
  });

  let mainEvent = data?.events[0];

  if (!!mainEvent?.festival) {
    mainEvent = mainEvent.festival;
  }

  const { data: pastEvents, loading: loadingPastEvents } =
    useGetPastEventsQuery({
      variables: {
        today,
      },
    });

  const mainEventStartTime =
    mainEvent?.start_time && DateObject(mainEvent.start_time);
  const mainEventEndTime =
    mainEvent?.end_time && DateObject(mainEvent.end_time);

  return (
    <>
      <Layout pageName='Events' className='space-y-4  px-4'>
        <h1 className='lg:sr-only text-center text-4xl bg-green-light text-white pt-6 pb-4 header'>
          Our Events
        </h1>
        {loading ? (
          <Spinner size={20} />
        ) : (
          <>
            {mainEvent && (
              <>
                <article className='max-w-screen-2xl w-full justify-around mx-auto flex-col md:flex-row md:flex-wrap lg:grid grid-cols-5 hidden'>
                  <header className=' flex flex-col  [ h-full w-full ] [ order-1  ] [ col-span-3 col-start-2 ]'>
                    <Link href={`/events/${mainEvent.slug}`}>
                      <a
                        className={`w-full h-full ${
                          !!mainEvent.is_festival
                            ? "ring-green-dark"
                            : "ring-yellow-dark"
                        } hover:ring-4`}>
                        <img
                          className={`${
                            !!mainEvent.is_festival
                              ? "bg-green-default text-white"
                              : "bg-yellow-default"
                          }  h-full w-full`}
                          src={mainEvent.image}
                          alt={`Cover image for event: ${mainEvent.title}`}
                        />
                      </a>
                    </Link>
                  </header>
                  <section
                    className={`${
                      !!mainEvent.is_festival
                        ? "bg-green-light text-white"
                        : "bg-yellow-light"
                    } flex [ flex-col mr-0 justify-between  space-y-4  ] [  order-2 md:order-1 p-4 md:w-1/2 lg:my-8  ] [ col-start-1 row-start-1 lg:w-full ]`}>
                    <span
                      className={`${
                        !!mainEvent.is_festival
                          ? "bg-green-dark text-white"
                          : "bg-yellow-dark"
                      }  mx-auto px-3 rounded-full text-white`}>
                      Next {mainEvent.is_festival ? "Festival" : "Event"}
                    </span>
                    <h2 className='header text-center text-2xl md:text-4xl'>
                      {mainEvent?.title}
                    </h2>
                    <dl className='mx-auto text-center'>
                      <dt className=''>
                        <h3 className='header text-2xl'>Date</h3>
                      </dt>
                      <dd className='pb-4'>
                        {mainEventStartTime.day}.{mainEventStartTime.month}.
                        {mainEventStartTime.year}
                      </dd>
                      <dt>
                        <h3 className='header text-2xl'>Start / End time</h3>
                      </dt>
                      <dd className='pb-4'>
                        {
                          <>
                            {mainEventStartTime.hour}:
                            {mainEventStartTime.minute} /{" "}
                            {mainEventEndTime.hour}:{mainEventEndTime.minute}
                          </>
                        }
                      </dd>
                      <dt className=''>
                        <h3 className='header text-2xl'>Featured Speakers</h3>
                      </dt>
                      <dd>{mainEvent.speakers}</dd>
                    </dl>

                    <Button
                      href={`/events/${mainEvent.slug}`}
                      buttonType='secondary'
                      buttonSize='xl'
                      className='mx-auto'>
                      View / Book
                    </Button>
                  </section>
                  {mainEvent.is_festival ? (
                    <>
                      <section
                        className={`bg-green-light text-white
                   flex  justify-center space-y-4 [ flex-col mr-0 ] [ order-3 md:order-2 p-4 md:w-1/2 lg:my-8 ] [ col-start-5 row-start-1 lg:w-full ]`}>
                        <h3 className='header text-3xl text-center w-full underline'>
                          Events
                        </h3>
                        <ul className='w-full text-lg space-y-4'>
                          {mainEvent.events.map(
                            (e, i) =>
                              i < 8 && (
                                <li className='w-full text-center'>
                                  <Link href={`/events/${e.slug}`}>
                                    <a className='text-black header mb-2 hover:bg-green-dark hover:text-white pt-1.5 px-2 bg-green-very-light'>
                                      {e.title}
                                    </a>
                                  </Link>
                                </li>
                              )
                          )}
                        </ul>
                        {mainEvent.events.length > 2 && (
                          <Link href={`/events/${mainEvent.slug}`}>
                            <a className='text-center text-lg italic hover:underline'>
                              ...More events on event page.
                            </a>
                          </Link>
                        )}
                      </section>
                    </>
                  ) : (
                    <section
                      className={`bg-yellow-light
                     flex  justify-center [ flex-col mr-0 ] [ order-3 md:order-2 p-4 md:w-1/2 lg:my-8 ] [ col-start-5 row-start-1 lg:w-full ]`}>
                      <h3 className='header text-2xl text-center w-full'>
                        Featured Book
                      </h3>
                      {mainEvent.event_books.length > 0 && (
                        <BookCard
                          book={mainEvent.event_books[0].book}
                          className='w-36 mx-auto'
                        />
                      )}
                    </section>
                  )}
                </article>
                <div className='w-full grid items-center grid-cols-1'>
                  <Button
                    href='#past_events'
                    buttonType='primary'
                    className='mx-auto'>
                    Watch Past Events
                  </Button>
                </div>
              </>
            )}
            <section className='py-8 mx-auto '>
              <div className='mx-auto prose'>
                <RenderContent
                  content={
                    content.data.find((block) => block.label === "about")
                      .data as string
                  }
                />
              </div>
            </section>
            {/* <section className='mx-auto text-center max-w-screen-2xl bg-yellow-light w-full py-8'>
              <h2 className='header text-2xl'>Book Clubs</h2>
            </section> */}
            <section className='mx-auto max-w-screen-2xl w-full py-8'>
              <h2 className='header text-4xl text-center pb-2 pt-4 sticky top-0 bg-white z-10'>
                All upcoming events
              </h2>
              {loading ? (
                <Spinner size={10} />
              ) : !data?.events ? (
                <p className='text-center text-xl'>No upcoming events</p>
              ) : (
                <ul className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8'>
                  {data.events?.map((e) => (
                    <li key={e.id}>
                      <EventCard event={e} className='' />
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section
              id='past_events'
              className='mx-auto max-w-screen-2xl w-full py-8'>
              <h2 className='header text-4xl text-center pb-2 pt-4 sticky top-0 bg-white z-10'>
                Watch Past events & Author Videos
              </h2>
              {loadingPastEvents ? (
                <Spinner size={10} />
              ) : !data?.events ? (
                <p className='text-center text-xl'>No upcoming events</p>
              ) : (
                <ul className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8'>
                  {pastEvents.events?.map((e) => (
                    <li key={e.id}>
                      <EventCard event={e} className='' />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </Layout>
    </>
  );
};

export default Events;
